import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { verifyEstimatorPassword, listEstimates, getEstimate, saveEstimate } from "@/lib/estimator.functions";
import { getStoredPassword, setStoredPassword } from "@/lib/estimator-session";
import { ArrowRight, Lock, Upload, Crop, Search, RotateCcw, Download, X, Plus, Trash2, Send, Loader2, Save, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const TYPE_PALETTE = [
  "#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#a855f7",
  "#06b6d4", "#ec4899", "#84cc16", "#f97316", "#6366f1",
];
function newTypeId() {
  return Math.random().toString(36).slice(2, 10);
}

export const Route = createFileRoute("/blueprint-counter")({
  validateSearch: (s: Record<string, unknown>) =>
    z.object({ estimateId: z.string().uuid().optional() }).parse(s),
  head: () => ({
    meta: [
      { title: "Blueprint Icon Counter | Apex AV" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: BlueprintCounterPage,
});

function BlueprintCounterPage() {
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && getStoredPassword()) setUnlocked(true);
  }, []);
  return unlocked ? <Counter /> : <PasswordGate onUnlock={() => setUnlocked(true)} />;
}

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const verify = useServerFn(verifyEstimatorPassword);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await verify({ data: { password } });
      if (res.ok) {
        setStoredPassword(password);
        onUnlock();
      } else setError("Incorrect password.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="container mx-auto px-6 py-24 flex items-center justify-center min-h-[70vh]">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl border border-border bg-card p-10 shadow-card">
        <div className="h-12 w-12 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow">
          <Lock className="h-6 w-6 text-brand-foreground" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold">Blueprint counter</h1>
        <p className="mt-2 text-sm text-muted-foreground">Enter the estimator password to continue.</p>
        <label className="block mt-8 text-sm font-medium">Password</label>
        <input
          type="password"
          aria-label="Password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 w-full rounded-md border border-border bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand"
        />
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={loading || !password}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow disabled:opacity-50"
        >
          {loading ? "Verifying…" : "Unlock"} <ArrowRight className="h-4 w-4" />
        </button>
      </form>
    </section>
  );
}


type IconType = { id: string; name: string; color: string; templateSize?: { w: number; h: number }; templateImage?: string };
type Match = { x: number; y: number; w: number; h: number; score: number; typeId: string; customShape?: string };

// Custom shape/icon library used in "Add custom" mode.
// `paths` are SVG path `d` strings drawn in a 24x24 viewBox (stroked).
// `shape` is a built-in geometric primitive drawn procedurally.
type CustomOption =
  | { id: string; label: string; kind: "shape"; shape: "box" | "circle" | "triangle" | "diamond" | "star" | "x" | "plus" }
  | { id: string; label: string; kind: "icon"; paths: string[] };

const CUSTOM_OPTIONS: CustomOption[] = [
  { id: "box", label: "Box", kind: "shape", shape: "box" },
  { id: "circle", label: "Circle", kind: "shape", shape: "circle" },
  { id: "triangle", label: "Triangle", kind: "shape", shape: "triangle" },
  { id: "diamond", label: "Diamond", kind: "shape", shape: "diamond" },
  { id: "star", label: "Star", kind: "shape", shape: "star" },
  { id: "x", label: "X", kind: "shape", shape: "x" },
  { id: "plus", label: "Plus", kind: "shape", shape: "plus" },
  // Icons (lucide-style strokes in a 24x24 viewBox)
  { id: "speaker", label: "Speaker", kind: "icon", paths: ["M4 5h16v14H4z", "M12 9.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z", "M12 7.5h.01"] },
  { id: "tv", label: "TV", kind: "icon", paths: ["M3 5h18v12H3z", "M8 21h8", "M12 17v4"] },
  { id: "camera", label: "Camera", kind: "icon", paths: ["M3 7h4l2-2h6l2 2h4v12H3z", "M12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"] },
  { id: "bulb", label: "Light", kind: "icon", paths: ["M9 18h6", "M10 21h4", "M12 3a6 6 0 0 1 4 10.5c-.7.7-1 1.5-1 2.5H9c0-1-.3-1.8-1-2.5A6 6 0 0 1 12 3z"] },
  { id: "wifi", label: "Wi-Fi", kind: "icon", paths: ["M2 9a15 15 0 0 1 20 0", "M5 12.5a10 10 0 0 1 14 0", "M8.5 16a5 5 0 0 1 7 0", "M12 20h.01"] },
  { id: "lock", label: "Lock", kind: "icon", paths: ["M5 11h14v10H5z", "M8 11V7a4 4 0 0 1 8 0v4"] },
  { id: "outlet", label: "Outlet", kind: "icon", paths: ["M4 4h16v16H4z", "M9 9v3", "M15 9v3", "M9 16h6"] },
  { id: "thermostat", label: "Thermostat", kind: "icon", paths: ["M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z", "M12 7v5l3 2"] },
  { id: "door", label: "Door", kind: "icon", paths: ["M6 3h12v18H6z", "M15 12h.01"] },
  { id: "monitor", label: "Display", kind: "icon", paths: ["M3 4h18v12H3z", "M8 20h8", "M12 16v4"] },
];

function drawCustom(
  ctx: CanvasRenderingContext2D,
  optionId: string,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
  highlighted: boolean,
) {
  const opt = CUSTOM_OPTIONS.find((o) => o.id === optionId);
  if (!opt) {
    ctx.strokeStyle = color;
    ctx.lineWidth = highlighted ? 4 : 2;
    ctx.strokeRect(x, y, w, h);
    return;
  }
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = highlighted ? 4 : 2.5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (opt.kind === "shape") {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const rx = w / 2;
    const ry = h / 2;
    ctx.beginPath();
    switch (opt.shape) {
      case "box":
        ctx.rect(x, y, w, h);
        break;
      case "circle":
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        break;
      case "triangle":
        ctx.moveTo(cx, y);
        ctx.lineTo(x + w, y + h);
        ctx.lineTo(x, y + h);
        ctx.closePath();
        break;
      case "diamond":
        ctx.moveTo(cx, y);
        ctx.lineTo(x + w, cy);
        ctx.lineTo(cx, y + h);
        ctx.lineTo(x, cy);
        ctx.closePath();
        break;
      case "star": {
        const spikes = 5;
        const outerR = Math.min(rx, ry);
        const innerR = outerR * 0.45;
        for (let i = 0; i < spikes * 2; i++) {
          const r = i % 2 === 0 ? outerR : innerR;
          const a = -Math.PI / 2 + (i * Math.PI) / spikes;
          const px = cx + Math.cos(a) * r;
          const py = cy + Math.sin(a) * r;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        break;
      }
      case "x":
        ctx.moveTo(x, y);
        ctx.lineTo(x + w, y + h);
        ctx.moveTo(x + w, y);
        ctx.lineTo(x, y + h);
        break;
      case "plus":
        ctx.moveTo(cx, y);
        ctx.lineTo(cx, y + h);
        ctx.moveTo(x, cy);
        ctx.lineTo(x + w, cy);
        break;
    }
    ctx.stroke();
  } else {
    // Icon: scale 24x24 viewBox into bounds
    ctx.translate(x, y);
    ctx.scale(w / 24, h / 24);
    ctx.lineWidth = (highlighted ? 4 : 2.5) / Math.max(w / 24, h / 24);
    for (const d of opt.paths) ctx.stroke(new Path2D(d));
  }
  ctx.restore();
}

function CustomPreview({ id, color = "currentColor" }: { id: string; color?: string }) {
  const opt = CUSTOM_OPTIONS.find((o) => o.id === id);
  if (!opt) return null;
  if (opt.kind === "icon") {
    return (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        {opt.paths.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </svg>
    );
  }
  // Shape preview: render via an offscreen-style inline svg
  const common = { fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (opt.shape) {
    case "box":
      return <svg viewBox="0 0 24 24" width="20" height="20" {...common}><rect x="3" y="3" width="18" height="18" /></svg>;
    case "circle":
      return <svg viewBox="0 0 24 24" width="20" height="20" {...common}><circle cx="12" cy="12" r="9" /></svg>;
    case "triangle":
      return <svg viewBox="0 0 24 24" width="20" height="20" {...common}><polygon points="12,3 22,21 2,21" /></svg>;
    case "diamond":
      return <svg viewBox="0 0 24 24" width="20" height="20" {...common}><polygon points="12,2 22,12 12,22 2,12" /></svg>;
    case "star":
      return <svg viewBox="0 0 24 24" width="20" height="20" {...common}><polygon points="12,2 14.6,9 22,9.5 16.3,14.2 18.2,21.5 12,17.5 5.8,21.5 7.7,14.2 2,9.5 9.4,9" /></svg>;
    case "x":
      return <svg viewBox="0 0 24 24" width="20" height="20" {...common}><line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" /></svg>;
    case "plus":
      return <svg viewBox="0 0 24 24" width="20" height="20" {...common}><line x1="12" y1="3" x2="12" y2="21" /><line x1="3" y1="12" x2="21" y2="12" /></svg>;
  }
}
type Rect = { x: number; y: number; w: number; h: number };
type PageInfo = { width: number; height: number; label: string };
type Selection = { pageIndex: number; rect: Rect };

// iOS Safari caps a single canvas at ~16M pixels (~4096x4096). Going larger
// silently fails to allocate and crashes the tab. Detect iOS and use a safe cap.
const IS_IOS =
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1));
const MAX_DIM = IS_IOS ? 4096 : 7200;
const PDF_RENDER_SCALE = IS_IOS ? 4 : 8; // initial render scale before fit-to-MAX_DIM
// iOS renders at lower resolution (smaller canvas cap), which makes template
// matching noisier and produces more false positives at the desktop default.
// Bump the default threshold on iOS to compensate.
const DEFAULT_THRESHOLD = IS_IOS ? 0.86 : 0.78;

function Counter() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const overlayRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const scrollRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Pre-rendered offscreen sources for each page; copied onto visible canvases via effect.
  const sourceCanvases = useRef<HTMLCanvasElement[]>([]);

  const [pages, setPages] = useState<PageInfo[]>([]);
  const [pageZoom, setPageZoom] = useState(1);
  const [loadingFile, setLoadingFile] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [dragStart, setDragStart] = useState<{ pageIndex: number; x: number; y: number } | null>(null);
  const [pageMatches, setPageMatches] = useState<Match[][]>([]);
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);
  const [searching, setSearching] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<{ pageIndex: number; matchIndex: number } | null>(null);
  const [addingLabel, setAddingLabel] = useState(false);
  const [customShape, setCustomShape] = useState<string>("box");

  const [types, setTypes] = useState<IconType[]>(() => [
    { id: newTypeId(), name: "Icon 1", color: TYPE_PALETTE[0] },
  ]);
  const [activeTypeId, setActiveTypeId] = useState<string>(() => "");

  // Push-to-estimate dialog
  const navigate = useNavigate();
  const search = Route.useSearch();
  const listFn = useServerFn(listEstimates);
  const getEstFn = useServerFn(getEstimate);
  const saveFn = useServerFn(saveEstimate);
  const [linkedEstimateId, setLinkedEstimateId] = useState<string | null>(search.estimateId ?? null);
  const [linkedEstimateName, setLinkedEstimateName] = useState<string | null>(null);
  const [loadingLinked, setLoadingLinked] = useState<boolean>(!!search.estimateId);
  const [pushOpen, setPushOpen] = useState(false);
  const [pushMode, setPushMode] = useState<"existing" | "new">(search.estimateId ? "existing" : "new");
  const [pushList, setPushList] = useState<Array<{ id: string; name: string; client_name: string | null }>>([]);
  const [pushListLoading, setPushListLoading] = useState(false);
  const [pushSelectedId, setPushSelectedId] = useState<string>(search.estimateId ?? "");
  const [pushNewClient, setPushNewClient] = useState("");
  const [pushNewName, setPushNewName] = useState("");
  const [pushing, setPushing] = useState(false);
  const [pushErr, setPushErr] = useState<string | null>(null);

  // Save / Save As project
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState("");
  const [saveAsClient, setSaveAsClient] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!pushOpen) return;
    const pw = getStoredPassword();
    if (!pw) return;
    setPushListLoading(true);
    setPushErr(null);
    listFn({ data: { password: pw } })
      .then((r) => {
        const items = (r.estimates ?? []).map((e: any) => ({ id: e.id, name: e.name, client_name: e.client_name }));
        setPushList(items);
        if (items.length > 0 && !pushSelectedId) setPushSelectedId(items[0].id);
      })
      .catch((e) => setPushErr(e instanceof Error ? e.message : "Failed to load estimates."))
      .finally(() => setPushListLoading(false));
  }, [pushOpen, listFn]);

  // Load a saved blueprint from a linked estimate (?estimateId=...).
  useEffect(() => {
    const estimateId = search.estimateId;
    if (!estimateId) return;
    const pw = getStoredPassword();
    if (!pw) return;
    let cancelled = false;
    setLoadingLinked(true);
    setError(null);
    (async () => {
      try {
        const { estimate } = await getEstFn({ data: { password: pw, id: estimateId } });
        if (cancelled) return;
        setLinkedEstimateName(estimate.name);
        const bp = ((estimate.data as any)?.blueprint ?? null) as
          | {
              sources?: { label: string; image: string; width: number; height: number }[];
              types?: IconType[];
              matches?: Match[][];
            }
          | null;
        if (!bp || !bp.sources || bp.sources.length === 0) {
          setError("This estimate has no editable blueprint saved. Older blueprints are view-only.");
          return;
        }
        // Load each source image into an offscreen canvas.
        const loaded: HTMLCanvasElement[] = [];
        const newPages: PageInfo[] = [];
        for (const s of bp.sources) {
          const img = await new Promise<HTMLImageElement>((res, rej) => {
            const im = new Image();
            im.onload = () => res(im);
            im.onerror = () => rej(new Error("Could not load blueprint image."));
            im.src = s.image;
          });
          if (cancelled) return;
          const off = document.createElement("canvas");
          off.width = s.width;
          off.height = s.height;
          off.getContext("2d")!.drawImage(img, 0, 0, s.width, s.height);
          loaded.push(off);
          newPages.push({ width: s.width, height: s.height, label: s.label });
        }
        if (cancelled) return;
        sourceCanvases.current = loaded;
        setPages(newPages);
        if (bp.types && bp.types.length > 0) {
          setTypes(bp.types);
          setActiveTypeId(bp.types[0].id);
        }
        setPageMatches(bp.matches ?? newPages.map(() => []));
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load blueprint.");
      } finally {
        if (!cancelled) setLoadingLinked(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.estimateId]);


  function buildBlueprintSnapshots() {
    const out: { label: string; image: string; width: number; height: number }[] = [];
    const MAX_W = 1600;
    pages.forEach((p, i) => {
      const src = sourceCanvases.current[i];
      if (!src) return;
      // Bake overlay onto a copy
      const baked = document.createElement("canvas");
      baked.width = src.width;
      baked.height = src.height;
      const bctx = baked.getContext("2d")!;
      bctx.drawImage(src, 0, 0);
      drawOverlay(i, null, pageMatches[i] ?? [], null);
      const ov = overlayRefs.current[i];
      if (ov) bctx.drawImage(ov, 0, 0);
      // Scale down for storage
      const scale = Math.min(1, MAX_W / baked.width);
      const w = Math.max(1, Math.round(baked.width * scale));
      const h = Math.max(1, Math.round(baked.height * scale));
      let finalCanvas = baked;
      if (scale < 1) {
        const small = document.createElement("canvas");
        small.width = w;
        small.height = h;
        small.getContext("2d")!.drawImage(baked, 0, 0, w, h);
        finalCanvas = small;
      }
      out.push({
        label: p.label,
        image: finalCanvas.toDataURL("image/jpeg", 0.82),
        width: w,
        height: h,
      });
    });
    return out;
  }

  // Raw page sources (no overlay) — used so a saved blueprint can be reopened and edited.
  function buildRawSources() {
    const out: { label: string; image: string; width: number; height: number }[] = [];
    const MAX_W = 2000;
    pages.forEach((p, i) => {
      const src = sourceCanvases.current[i];
      if (!src) return;
      const scale = Math.min(1, MAX_W / src.width);
      const w = Math.max(1, Math.round(src.width * scale));
      const h = Math.max(1, Math.round(src.height * scale));
      let canvas: HTMLCanvasElement = src;
      if (scale < 1) {
        const small = document.createElement("canvas");
        small.width = w;
        small.height = h;
        small.getContext("2d")!.drawImage(src, 0, 0, w, h);
        canvas = small;
      }
      out.push({
        label: p.label,
        image: canvas.toDataURL("image/jpeg", 0.85),
        width: w,
        height: h,
      });
    });
    return out;
  }

  async function handlePush() {
    const pw = getStoredPassword();
    if (!pw) {
      setPushErr("Session expired. Please unlock again.");
      return;
    }
    // Build extras from icon types with counts > 0
    const extras = types
      .map((t) => ({
        t,
        count: pageMatches.reduce((s, arr) => s + arr.filter((m) => m.typeId === t.id).length, 0),
      }))
      .filter((x) => x.count > 0)
      .map((x) => ({
        id: (typeof crypto !== "undefined" && crypto.randomUUID?.()) || `${Date.now()}-${Math.random()}`,
        label: x.t.name,
        qty: x.count,
        price: 0,
        fromBlueprint: true as const,
      }));
    if (extras.length === 0) {
      setPushErr("No icons counted yet.");
      return;
    }
    setPushing(true);
    setPushErr(null);
    try {
      const snapshots = buildBlueprintSnapshots();
      const sources = buildRawSources();
      // Scale matches to the saved source dimensions so they re-align on reload.
      const scaledMatches = pages.map((p, i) => {
        const s = sources[i];
        if (!s) return [] as typeof pageMatches[number];
        const sx = s.width / p.width;
        const sy = s.height / p.height;
        return (pageMatches[i] ?? []).map((m) => ({
          ...m,
          x: Math.round(m.x * sx),
          y: Math.round(m.y * sy),
          w: Math.max(1, Math.round(m.w * sx)),
          h: Math.max(1, Math.round(m.h * sy)),
        }));
      });
      const blueprint = {
        pages: snapshots,
        pageCount: snapshots.length,
        pushedAt: new Date().toISOString(),
        sources,
        types,
        matches: scaledMatches,
      };


      let targetId: string | undefined;
      let name = "";
      let clientName: string | null = null;
      let data: any = { sel: {}, photos: [], version: 1 };
      let total = 0;

      if (pushMode === "existing") {
        if (!pushSelectedId) throw new Error("Pick an estimate.");
        const { estimate } = await getEstFn({ data: { password: pw, id: pushSelectedId } });
        targetId = estimate.id;
        name = estimate.name;
        clientName = estimate.client_name ?? null;
        data = estimate.data ?? { sel: {}, photos: [] };
        total = Number(estimate.total ?? 0);
        if (!data.sel) data.sel = {};
        const cur = data.sel.prewire ?? {};
        // Strip previously-pushed blueprint extras so re-saving updates instead of duplicating.
        const keepExtras = ((cur.extras as any[]) ?? []).filter((e) => !e?.fromBlueprint);
        data.sel.prewire = {
          ...cur,
          enabled: true,
          extras: [...keepExtras, ...extras],
          tier: cur.tier ?? 0,
          qty: cur.qty ?? 1,
          customPrice: cur.customPrice ?? 0,
          customLabel: cur.customLabel ?? "",
          componentQtys: cur.componentQtys ?? {},
          upchargePct: cur.upchargePct ?? 0,
        };
        data.blueprint = blueprint;
      } else {
        clientName = pushNewClient.trim() || null;
        name = pushNewName.trim() || (clientName ? `${clientName} estimate` : "");
        if (!name) throw new Error("Enter a client name or estimate name.");
        data = {
          sel: {
            prewire: {
              enabled: true,
              tier: 0,
              qty: 1,
              customPrice: 0,
              customLabel: "",
              componentQtys: {},
              upchargePct: 0,
              extras,
            },
          },
          photos: [],
          version: 1,
          blueprint,
        };
      }

      const res = await saveFn({
        data: {
          password: pw,
          id: targetId,
          name,
          client_name: clientName,
          client_email: null,
          client_phone: null,
          client_address: null,
          notes: null,
          total,
          data,
        },
      });
      setPushOpen(false);
      navigate({ to: "/estimator", search: { id: res.id } });
    } catch (e) {
      setPushErr(e instanceof Error ? e.message : "Failed to push.");
    } finally {
      setPushing(false);
    }
  }

  // Save the blueprint project (sources, types, matches) to a saved_estimates row.
  // If `id` is provided, updates that row in place; otherwise creates a new one.
  async function doSaveProject(opts: { id?: string; name: string; clientName: string | null }) {
    const pw = getStoredPassword();
    if (!pw) throw new Error("Session expired. Unlock again.");
    if (pages.length === 0) throw new Error("Upload a blueprint first.");

    const snapshots = buildBlueprintSnapshots();
    const sources = buildRawSources();
    const scaledMatches = pages.map((p, i) => {
      const s = sources[i];
      if (!s) return [] as typeof pageMatches[number];
      const sx = s.width / p.width;
      const sy = s.height / p.height;
      return (pageMatches[i] ?? []).map((m) => ({
        ...m,
        x: Math.round(m.x * sx),
        y: Math.round(m.y * sy),
        w: Math.max(1, Math.round(m.w * sx)),
        h: Math.max(1, Math.round(m.h * sy)),
      }));
    });
    const extras = types
      .map((t) => ({
        t,
        count: pageMatches.reduce((s, arr) => s + arr.filter((m) => m.typeId === t.id).length, 0),
      }))
      .filter((x) => x.count > 0)
      .map((x) => ({
        id: (typeof crypto !== "undefined" && crypto.randomUUID?.()) || `${Date.now()}-${Math.random()}`,
        label: x.t.name,
        qty: x.count,
        price: 0,
        fromBlueprint: true as const,
      }));
    const blueprint = {
      pages: snapshots,
      pageCount: snapshots.length,
      pushedAt: new Date().toISOString(),
      sources,
      types,
      matches: scaledMatches,
    };

    let name = opts.name;
    let clientName = opts.clientName;
    let data: any = { sel: {}, photos: [], version: 1, blueprint };
    let total = 0;

    if (opts.id) {
      const { estimate } = await getEstFn({ data: { password: pw, id: opts.id } });
      if (!opts.name) name = estimate.name;
      if (opts.clientName == null) clientName = estimate.client_name ?? null;
      data = estimate.data ?? { sel: {}, photos: [] };
      total = Number(estimate.total ?? 0);
      if (!data.sel) data.sel = {};
      const cur = data.sel.prewire ?? {};
      const keepExtras = ((cur.extras as any[]) ?? []).filter((e) => !e?.fromBlueprint);
      data.sel.prewire = {
        ...cur,
        enabled: true,
        extras: [...keepExtras, ...extras],
        tier: cur.tier ?? 0,
        qty: cur.qty ?? 1,
        customPrice: cur.customPrice ?? 0,
        customLabel: cur.customLabel ?? "",
        componentQtys: cur.componentQtys ?? {},
        upchargePct: cur.upchargePct ?? 0,
      };
      data.blueprint = blueprint;
    } else {
      data = {
        sel: {
          prewire: {
            enabled: true,
            tier: 0,
            qty: 1,
            customPrice: 0,
            customLabel: "",
            componentQtys: {},
            upchargePct: 0,
            extras,
          },
        },
        photos: [],
        version: 1,
        blueprint,
      };
    }

    const res = await saveFn({
      data: {
        password: pw,
        id: opts.id,
        name,
        client_name: clientName,
        client_email: null,
        client_phone: null,
        client_address: null,
        notes: null,
        total,
        data,
      },
    });
    setLinkedEstimateId(res.id);
    setLinkedEstimateName(name);
    setSavedAt(Date.now());
    return res.id;
  }

  async function handleSave() {
    setSaveErr(null);
    if (!linkedEstimateId) {
      // No project yet — fall through to Save As.
      setSaveAsName("");
      setSaveAsClient("");
      setSaveAsOpen(true);
      return;
    }
    setSaving(true);
    try {
      await doSaveProject({ id: linkedEstimateId, name: linkedEstimateName ?? "", clientName: null });
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  function handleSaveAs() {
    setSaveErr(null);
    setSaveAsName(linkedEstimateName ? `${linkedEstimateName} (copy)` : "");
    setSaveAsClient("");
    setSaveAsOpen(true);
  }

  async function handleSaveAsConfirm() {
    setSaveErr(null);
    const clientName = saveAsClient.trim() || null;
    const name = saveAsName.trim() || (clientName ? `${clientName} blueprint` : "");
    if (!name) {
      setSaveErr("Enter a project name or client name.");
      return;
    }
    setSaving(true);
    try {
      await doSaveProject({ name, clientName });
      setSaveAsOpen(false);
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }



  // Ensure activeTypeId points at a real type
  useEffect(() => {
    if (!types.find((t) => t.id === activeTypeId)) {
      setActiveTypeId(types[0]?.id ?? "");
    }
  }, [types, activeTypeId]);
  const activeType = types.find((t) => t.id === activeTypeId) ?? types[0];
  const typeById = (id: string) => types.find((t) => t.id === id);

  function addType() {
    const id = newTypeId();
    const color = TYPE_PALETTE[types.length % TYPE_PALETTE.length];
    setTypes((prev) => [...prev, { id, name: `Icon ${prev.length + 1}`, color }]);
    setActiveTypeId(id);
  }
  function updateType(id: string, patch: Partial<IconType>) {
    setTypes((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }
  function deleteType(id: string) {
    if (types.length <= 1) return;
    setTypes((prev) => prev.filter((t) => t.id !== id));
    setPageMatches((prev) => prev.map((arr) => arr.filter((m) => m.typeId !== id)));
  }

  async function handleFiles(files: File[]) {
    if (files.length === 0) return;
    setError(null);
    setSelection(null);
    setPageMatches([]);
    setPages([]);
    sourceCanvases.current = [];
    setLoadingFile(true);
    const allPages: PageInfo[] = [];
    const allOffscreens: HTMLCanvasElement[] = [];
    try {
      for (const file of files) {
        if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) {
          const { pages: ps, offscreens } = await loadPdf(file);
          allPages.push(...ps);
          allOffscreens.push(...offscreens);
        } else {
          const { page, offscreen } = await loadImage(file);
          allPages.push(page);
          allOffscreens.push(offscreen);
        }
      }
      sourceCanvases.current = allOffscreens;
      setPages(allPages);
    } catch (e) {
      console.error("[blueprint-counter] load failed", e);
      setError(e instanceof Error ? e.message : "Could not load file.");
    } finally {
      setLoadingFile(false);
    }
  }

  async function loadImage(file: File): Promise<{ page: PageInfo; offscreen: HTMLCanvasElement }> {
    const dataUrl: string = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result as string);
      r.onerror = () => rej(new Error("Read failed"));
      r.readAsDataURL(file);
    });
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error("Could not load image. Use PNG/JPG."));
      im.src = dataUrl;
    });
    let { width, height } = img;
    const scale = Math.min(1, MAX_DIM / Math.max(width, height));
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    const off = document.createElement("canvas");
    off.width = width;
    off.height = height;
    off.getContext("2d")!.drawImage(img, 0, 0, width, height);
    return { page: { width, height, label: file.name }, offscreen: off };
  }

  async function loadPdf(file: File): Promise<{ pages: PageInfo[]; offscreens: HTMLCanvasElement[] }> {
    const pdfjs: any = await import("pdfjs-dist");
    // Use a fake worker (main-thread) — most reliable across Vite/Cloudflare setups.
    // pdf.js will run synchronously on the main thread, which is fine for a few pages.
    if (!pdfjs.GlobalWorkerOptions.workerSrc && !pdfjs.GlobalWorkerOptions.workerPort) {
      try {
        const workerMod: any = await import("pdfjs-dist/build/pdf.worker.min.mjs?worker");
        pdfjs.GlobalWorkerOptions.workerPort = new workerMod.default();
      } catch (err) {
        console.warn("[blueprint-counter] worker import failed, falling back to main-thread", err);
        // Fallback: set a dummy workerSrc; pdf.js will use a fake worker on main thread.
        pdfjs.GlobalWorkerOptions.workerSrc = "";
      }
    }
    const buf = await file.arrayBuffer();
    const doc = await pdfjs.getDocument({ data: buf, disableWorker: !pdfjs.GlobalWorkerOptions.workerPort }).promise;
    const newPages: PageInfo[] = [];
    const offscreens: HTMLCanvasElement[] = [];
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const baseViewport = page.getViewport({ scale: 1 });
      const maxSide = Math.max(baseViewport.width, baseViewport.height) * PDF_RENDER_SCALE;
      const fit = Math.min(1, MAX_DIM / maxSide);
      const scale = PDF_RENDER_SCALE * fit;
      const viewport = page.getViewport({ scale });
      const w = Math.round(viewport.width);
      const h = Math.round(viewport.height);
      const off = document.createElement("canvas");
      off.width = w;
      off.height = h;
      const ctx = off.getContext("2d")!;
      // White background — PDFs render transparent otherwise.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
      await page.render({ canvas: off, canvasContext: ctx, viewport }).promise;
      offscreens.push(off);
      newPages.push({ width: w, height: h, label: `${file.name} — page ${i} of ${doc.numPages}` });
    }
    return { pages: newPages, offscreens };
  }

  // Copy offscreen sources onto the mounted canvases whenever pages change.
  useEffect(() => {
    pages.forEach((p, i) => {
      const c = canvasRefs.current[i];
      const o = overlayRefs.current[i];
      const src = sourceCanvases.current[i];
      if (!c || !o || !src) return;
      c.width = o.width = p.width;
      c.height = o.height = p.height;
      c.getContext("2d")!.drawImage(src, 0, 0);
      o.getContext("2d")!.clearRect(0, 0, p.width, p.height);
    });
  }, [pages]);

  function canvasCoords(e: React.MouseEvent<HTMLCanvasElement>, pageIndex: number) {
    const o = overlayRefs.current[pageIndex]!;
    const rect = o.getBoundingClientRect();
    const scaleX = o.width / rect.width;
    const scaleY = o.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>, pageIndex: number) {
    if (addingLabel) {
      if (!activeType) return;
      const p = canvasCoords(e, pageIndex);
      const size = activeType.templateSize ?? (selection ? { w: selection.rect.w, h: selection.rect.h } : { w: 40, h: 40 });
      const w = Math.max(8, Math.round(size.w));
      const h = Math.max(8, Math.round(size.h));
      const newMatch: Match = { x: Math.round(p.x - w / 2), y: Math.round(p.y - h / 2), w, h, score: 1, typeId: activeType.id, customShape };
      setPageMatches((prev) => {
        const next = pages.map((_, i) => (prev[i] ? prev[i].slice() : []));
        next[pageIndex] = [...next[pageIndex], newMatch];
        return next;
      });
      return;
    }
    if (!selecting) return;
    e.preventDefault();
    try {
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    } catch {}
    const p = canvasCoords(e, pageIndex);
    setDragStart({ pageIndex, x: p.x, y: p.y });
    setSelection({ pageIndex, rect: { x: p.x, y: p.y, w: 0, h: 0 } });
  }
  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>, pageIndex: number) {
    if (!dragStart || dragStart.pageIndex !== pageIndex) return;
    e.preventDefault();
    const p = canvasCoords(e, pageIndex);
    const x = Math.min(dragStart.x, p.x);
    const y = Math.min(dragStart.y, p.y);
    const w = Math.abs(p.x - dragStart.x);
    const h = Math.abs(p.y - dragStart.y);
    setSelection({ pageIndex, rect: { x, y, w, h } });
  }
  function onPointerUp() {
    if (!dragStart) return;
    setDragStart(null);
    setSelecting(false);
    // Open zoom preview if a usable selection was drawn
    setSelection((prev) => {
      if (prev && prev.rect.w >= 6 && prev.rect.h >= 6) {
        setPreviewOpen(true);
      }
      return prev;
    });
  }

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(8);

  function acceptSelection(rect: Rect) {
    setSelection((prev) => (prev ? { ...prev, rect } : prev));
    setPreviewOpen(false);
    if (activeType && selection) {
      const src = sourceCanvases.current[selection.pageIndex];
      if (src) {
        try {
          const off = document.createElement("canvas");
          const maxSize = 40;
          const scale = Math.min(1, maxSize / Math.max(rect.w, rect.h));
          off.width = Math.max(1, Math.round(rect.w * scale));
          off.height = Math.max(1, Math.round(rect.h * scale));
          const ctx = off.getContext("2d")!;
          ctx.drawImage(src, rect.x, rect.y, rect.w, rect.h, 0, 0, off.width, off.height);
          updateType(activeType.id, { templateImage: off.toDataURL("image/png") });
        } catch {
          // ignore thumbnail extraction errors
        }
      }
    }
  }
  function declineSelection() {
    setPreviewOpen(false);
    setSelection(null);
  }


  function drawOverlay(pageIndex: number, sel: Rect | null, ms: Match[], hi: number | null) {
    const o = overlayRefs.current[pageIndex];
    if (!o) return;
    const ctx = o.getContext("2d")!;
    ctx.clearRect(0, 0, o.width, o.height);
    if (sel && sel.w > 0 && sel.h > 0) {
      ctx.strokeStyle = "#ef4444";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(sel.x, sel.y, sel.w, sel.h);
      ctx.setLineDash([]);
    }
    if (ms.length) {
      // per-type counters so labels are numbered within each icon type
      const perTypeIdx: Record<string, number> = {};
      ms.forEach((m, i) => {
        const t = typeById(m.typeId);
        const baseColor = t?.color ?? "#10b981";
        const isHi = hi === i;
        const color = isHi ? "#f59e0b" : baseColor;
        if (m.customShape) {
          drawCustom(ctx, m.customShape, m.x, m.y, m.w, m.h, color, isHi);
        } else {
          ctx.strokeStyle = color;
          ctx.lineWidth = isHi ? 4 : 2;
          ctx.strokeRect(m.x, m.y, m.w, m.h);
        }

        perTypeIdx[m.typeId] = (perTypeIdx[m.typeId] ?? 0) + 1;
        const label = `${pageIndex + 1}-${perTypeIdx[m.typeId]}`;
        const fontSize = Math.max(14, Math.round(Math.min(m.w, m.h) * 0.55));
        ctx.font = `bold ${fontSize}px sans-serif`;
        ctx.textBaseline = "middle";
        ctx.textAlign = "center";
        const padX = 6;
        const padY = 4;
        const textW = ctx.measureText(label).width;
        const boxW = textW + padX * 2;
        const boxH = fontSize + padY * 2;
        const gap = Math.max(8, Math.round(Math.min(m.w, m.h) * 0.35));

        const ax = m.x + m.w;
        const ay = m.y;
        let bx = ax + gap;
        let by = ay - gap - boxH;
        if (bx + boxW > o.width) bx = m.x - gap - boxW;
        if (by < 0) by = ay + gap;
        const bcx = bx + boxW / 2;
        const bcy = by + boxH / 2;

        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(bcx, bcy);
        ctx.stroke();

        ctx.fillStyle = color;
        ctx.fillRect(bx, by, boxW, boxH);
        ctx.fillStyle = "#ffffff";
        ctx.fillText(label, bcx, bcy + 1);
      });
    }
  }

  // Redraw overlays whenever selection/matches/highlight change
  useEffect(() => {
    pages.forEach((_, i) => {
      const sel = selection && selection.pageIndex === i ? selection.rect : null;
      const ms = pageMatches[i] ?? [];
      const hi = highlight && highlight.pageIndex === i ? highlight.matchIndex : null;
      drawOverlay(i, sel, ms, hi);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages.length, pageMatches, selection?.pageIndex, selection?.rect.x, selection?.rect.y, selection?.rect.w, selection?.rect.h, highlight?.pageIndex, highlight?.matchIndex]);

  function deleteMatch(pageIndex: number, matchIndex: number) {
    setPageMatches((prev) => {
      const next = prev.map((arr) => arr.slice());
      if (next[pageIndex]) next[pageIndex].splice(matchIndex, 1);
      return next;
    });
    setHighlight(null);
  }

  function jumpToLabel(pageIndex: number, matchIndex: number) {
    setHighlight({ pageIndex, matchIndex });
    const m = pageMatches[pageIndex]?.[matchIndex];
    const o = overlayRefs.current[pageIndex];
    const wrap = scrollRefs.current[pageIndex];
    if (m && o && wrap) {
      const cssScale = o.clientWidth / o.width;
      const cx = (m.x + m.w / 2) * cssScale;
      const cy = (m.y + m.h / 2) * cssScale;
      wrap.scrollTo({
        left: Math.max(0, cx - wrap.clientWidth / 2),
        top: Math.max(0, cy - wrap.clientHeight / 2),
        behavior: "smooth",
      });
    }
  }

  async function runSearch() {
    if (!selection || selection.rect.w < 6 || selection.rect.h < 6) {
      setError("Draw a selection rectangle around an icon first.");
      return;
    }
    if (!activeType) {
      setError("Create an icon type first.");
      return;
    }
    const typeId = activeType.id;
    setError(null);
    setSearching(true);
    setProgress(0);
    // Clear only this type's previous matches; keep others.
    setPageMatches((prev) => {
      const base = pages.map((_, i) => (prev[i] ? prev[i].filter((m) => m.typeId !== typeId) : []));
      return base;
    });
    try {
      const srcCanvas = canvasRefs.current[selection.pageIndex]!;
      const srcCtx = srcCanvas.getContext("2d", { willReadFrequently: true })!;
      const srcImg = srcCtx.getImageData(0, 0, srcCanvas.width, srcCanvas.height);
      const sel = {
        x: Math.round(selection.rect.x),
        y: Math.round(selection.rect.y),
        w: Math.round(selection.rect.w),
        h: Math.round(selection.rect.h),
      };
      const baseTemplate = extractTemplate(srcImg, sel);
      updateType(typeId, { templateSize: { w: sel.w, h: sel.h } });
      const templates = [
        baseTemplate,
        rotateTemplate(baseTemplate, 1),
        rotateTemplate(baseTemplate, 2),
        rotateTemplate(baseTemplate, 3),
      ];

      const perPageNew: Match[][] = [];
      for (let i = 0; i < pages.length; i++) {
        const c = canvasRefs.current[i]!;
        const ctx = c.getContext("2d", { willReadFrequently: true })!;
        const img = ctx.getImageData(0, 0, c.width, c.height);
        const raw = await templateMatchMulti(img, templates, threshold, (p) => {
          setProgress((i + p) / pages.length);
        });
        const tagged: Match[] = raw.map((m) => ({ ...m, typeId }));
        perPageNew.push(tagged);
        setPageMatches((prev) => {
          const next = pages.map((_, pi) =>
            pi <= i
              ? [...(prev[pi] ?? []).filter((m) => m.typeId !== typeId), ...(pi === i ? tagged : (perPageNew[pi] ?? []))]
              : (prev[pi] ?? []).filter((m) => m.typeId !== typeId),
          );
          return next;
        });
      }
      setProgress(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Search failed.");
    } finally {
      setSearching(false);
    }
  }

  function reset() {
    setPageMatches([]);
    setSelection(null);
  }

  function downloadPage(pageIndex: number) {
    const c = canvasRefs.current[pageIndex]!;
    const o = overlayRefs.current[pageIndex]!;
    const out = document.createElement("canvas");
    out.width = c.width;
    out.height = c.height;
    const octx = out.getContext("2d")!;
    octx.drawImage(c, 0, 0);
    octx.drawImage(o, 0, 0);
    const a = document.createElement("a");
    const count = pageMatches[pageIndex]?.length ?? 0;
    a.download = `blueprint-page-${pageIndex + 1}-count-${count}.png`;
    a.href = out.toDataURL("image/png");
    a.click();
  }

  const totalCount = pageMatches.reduce((s, p) => s + p.length, 0);
  const hasResults = pageMatches.some((p) => p.length > 0);

  return (
    <section className="container mx-auto px-6 pt-16 pb-56">
      <h1 className="font-display text-4xl md:text-5xl font-bold">Blueprint icon counter</h1>
      <p className="mt-2 text-muted-foreground">
        Upload a blueprint image or multi-page PDF, drag a box around an icon, then count every instance across all pages.
      </p>

      {linkedEstimateId && (
        <div className="mt-4 rounded-md border border-brand/40 bg-brand/5 px-4 py-2.5 text-sm flex items-center gap-2 flex-wrap">
          {loadingLinked ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading saved blueprint…</span>
            </>
          ) : (
            <>
              <span className="text-muted-foreground">Editing blueprint for estimate:</span>
              <span className="font-medium">{linkedEstimateName ?? "—"}</span>
              <button
                type="button"
                onClick={() => navigate({ to: "/estimator", search: { id: linkedEstimateId } })}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1 text-xs font-medium hover:bg-muted"
              >
                Open estimate <ArrowRight className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur border-t border-border">
        <div className="px-6 pt-3 pb-2 border-b border-border/60 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Icon types</span>
          {types.map((t) => {
            const isActive = t.id === activeTypeId;
            const count = pageMatches.reduce((s, arr) => s + arr.filter((m) => m.typeId === t.id).length, 0);
            return (
              <span
                key={t.id}
                className={`inline-flex items-center gap-1.5 rounded-md border pl-1.5 pr-1 py-1 text-xs transition-colors ${
                  isActive ? "bg-accent" : "bg-muted/40 hover:bg-muted"
                }`}
                style={{ borderColor: t.color }}
              >
                <button
                  type="button"
                  onClick={() => setActiveTypeId(t.id)}
                  className="inline-flex items-center gap-1.5"
                  title="Make active"
                >
                  {t.templateImage ? (
                    <img
                      src={t.templateImage}
                      alt=""
                      className="h-6 w-6 rounded-sm border border-border object-contain"
                    />
                  ) : (
                    <span className="h-3 w-3 rounded-sm" style={{ background: t.color }} />
                  )}
                  <input
                    type="color"
                    value={t.color}
                    onChange={(e) => updateType(t.id, { color: e.target.value })}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 cursor-pointer rounded border-0 bg-transparent p-0"
                    aria-label={`Color for ${t.name}`}
                  />
                  <input
                    type="text"
                    value={t.name}
                    onChange={(e) => updateType(t.id, { name: e.target.value })}
                    onClick={(e) => e.stopPropagation()}
                    className="w-24 bg-transparent text-xs font-medium focus:outline-none focus:ring-1 focus:ring-ring rounded px-1"
                    aria-label="Icon type name"
                  />
                  <span className="font-mono text-[10px] text-muted-foreground">{count}</span>
                </button>
                {types.length > 1 && (
                  <button
                    type="button"
                    onClick={() => deleteType(t.id)}
                    aria-label={`Delete ${t.name}`}
                    className="opacity-50 hover:opacity-100 hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
          <button
            type="button"
            onClick={addType}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 text-xs hover:bg-muted"
          >
            <Plus className="h-3 w-3" /> New type
          </button>
        </div>
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = e.target.files ? Array.from(e.target.files) : [];
            if (files.length) handleFiles(files);
            e.target.value = "";
          }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={loadingFile}
          className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-5 py-2.5 text-sm font-semibold text-brand-foreground shadow-glow disabled:opacity-50"
        >
          <Upload className="h-4 w-4" /> {loadingFile ? "Loading…" : "Upload"}
        </button>
        <button
          onClick={() => {
            setAddingLabel(false);
            setSelecting(true);
            if (activeType) {
              const tid = activeType.id;
              setPageMatches((prev) => prev.map((arr) => arr.filter((m) => m.typeId !== tid)));
            }
          }}
          disabled={pages.length === 0 || !activeType}
          className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
          style={activeType ? { borderColor: activeType.color } : undefined}
        >
          <Crop className="h-4 w-4" /> {selecting ? "Drag on any page…" : `Select icon${activeType ? ` (${activeType.name})` : ""}`}
        </button>
        <button
          onClick={runSearch}
          disabled={!selection || searching}
          className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2.5 text-sm font-semibold text-background disabled:opacity-50"
        >
          <Search className="h-4 w-4" /> {searching ? `Searching… ${Math.round(progress * 100)}%` : "Count instances"}
        </button>
        <button
          onClick={() => {
            setSelecting(false);
            setAddingLabel((v) => !v);
          }}
          disabled={pages.length === 0}
          className={`inline-flex items-center gap-2 rounded-md border px-4 py-2.5 text-sm font-medium disabled:opacity-50 ${
            addingLabel ? "border-amber-500 bg-amber-500/15 text-amber-600" : "border-border hover:bg-muted"
          }`}
        >
          <Plus className="h-4 w-4" /> {addingLabel ? "Click on page to add… (done)" : "Add custom"}
        </button>
        {addingLabel && (
          <div className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-1 flex-wrap max-w-full">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground px-1">Shape</span>
            {CUSTOM_OPTIONS.map((opt) => {
              const isActive = customShape === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setCustomShape(opt.id)}
                  title={opt.label}
                  aria-label={opt.label}
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-sm border transition-colors ${
                    isActive
                      ? "border-amber-500 bg-amber-500/15 text-amber-600"
                      : "border-transparent text-foreground/70 hover:bg-muted hover:text-foreground"
                  }`}
                  style={isActive && activeType ? { color: activeType.color, borderColor: activeType.color } : undefined}
                >
                  <CustomPreview id={opt.id} />
                </button>
              );
            })}
          </div>
        )}
        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          Sensitivity
          <input
            type="range"
            min={0.5}
            max={0.95}
            step={0.01}
            value={threshold}
            onChange={(e) => setThreshold(parseFloat(e.target.value))}
          />
          <span className="font-mono text-xs">{threshold.toFixed(2)}</span>
        </label>
        {hasResults && (
          <span className="ml-auto inline-flex items-baseline gap-2 rounded-md bg-muted px-3 py-1.5 text-sm">
            <span className="text-muted-foreground uppercase tracking-wider text-xs">Total</span>
            <span className="font-display text-xl font-bold">{totalCount}</span>
          </span>
        )}
        {pages.length > 0 && (
          <button
            onClick={handleSave}
            disabled={saving}
            title={linkedEstimateId ? "Save changes to this project" : "Save as new project"}
            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : savedAt && Date.now() - savedAt < 2500 ? <Check className="h-4 w-4 text-emerald-500" /> : <Save className="h-4 w-4" />}
            Save
          </button>
        )}
        {pages.length > 0 && (
          <button
            onClick={handleSaveAs}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> Save as…
          </button>
        )}
        {hasResults && (
          <button
            onClick={() => setPushOpen(true)}
            className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-4 py-2.5 text-sm font-semibold text-brand-foreground shadow-glow"
          >
            <Send className="h-4 w-4" /> Push to estimate
          </button>
        )}
        {pages.length > 0 && (
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted"
          >
            <RotateCcw className="h-4 w-4" /> Reset
          </button>
        )}
        </div>
      </div>

      {error && <p className="mt-4 text-sm text-destructive">{error}</p>}
      {saveErr && !saveAsOpen && <p className="mt-2 text-sm text-destructive">{saveErr}</p>}

      {hasResults && (
        <div className="mt-6 rounded-xl border border-border bg-card p-6 shadow-card">
          <div className="flex flex-wrap items-baseline gap-6">
            <div>
              <div className="text-sm uppercase tracking-wider text-muted-foreground">Total across all pages</div>
              <div className="font-display text-5xl font-bold mt-1">{totalCount}</div>
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-sm uppercase tracking-wider text-muted-foreground mb-2">By type</div>
              <div className="flex flex-wrap gap-2">
                {types.map((t) => {
                  const count = pageMatches.reduce((s, arr) => s + arr.filter((m) => m.typeId === t.id).length, 0);
                  if (count === 0) return null;
                  return (
                    <span key={t.id} className="inline-flex items-center gap-2 rounded-md bg-muted px-3 py-1.5 text-sm">
                      <span className="h-3 w-3 rounded-sm" style={{ background: t.color }} />
                      <span className="text-muted-foreground">{t.name}:</span>
                      <span className="font-semibold">{count}</span>
                    </span>
                  );
                })}
              </div>
            </div>
            {pages.length > 1 && (
              <div className="flex-1 min-w-[240px]">
                <div className="text-sm uppercase tracking-wider text-muted-foreground mb-2">Per page</div>
                <div className="flex flex-wrap gap-2">
                  {pages.map((p, i) => (
                    <span key={i} className="inline-flex items-center gap-2 rounded-md bg-muted px-3 py-1.5 text-sm">
                      <span className="text-muted-foreground">{p.label}:</span>
                      <span className="font-semibold">{pageMatches[i]?.length ?? 0}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {pages.length === 0 ? (
        <div className="mt-6 rounded-xl border border-border bg-card p-16 text-center text-muted-foreground shadow-card">
          <Upload className="h-10 w-10 mx-auto mb-3 opacity-50" />
          Upload a blueprint to get started.
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {pages.map((p, i) => (
            <div key={i} className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
              <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
                <div className="text-sm font-medium">
                  {p.label}
                  {pageMatches[i] !== undefined && (
                    <span className="ml-3 text-muted-foreground">
                      — {pageMatches[i].length} found
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="inline-flex items-center rounded-md border border-border overflow-hidden">
                    <button
                      onClick={() => setPageZoom((z) => Math.max(1, +(z - 0.5).toFixed(2)))}
                      className="px-2 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-40"
                      disabled={pageZoom <= 1}
                      aria-label="Zoom out"
                    >
                      −
                    </button>
                    <span className="px-2 py-1.5 text-xs font-mono tabular-nums w-12 text-center">
                      {Math.round(pageZoom * 100)}%
                    </span>
                    <button
                      onClick={() => setPageZoom((z) => Math.min(6, +(z + 0.5).toFixed(2)))}
                      className="px-2 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-40"
                      disabled={pageZoom >= 6}
                      aria-label="Zoom in"
                    >
                      +
                    </button>
                  </div>
                  {(pageMatches[i]?.length ?? 0) > 0 && (
                    <button
                      onClick={() => downloadPage(i)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
                    >
                      <Download className="h-3.5 w-3.5" /> Download
                    </button>
                  )}
                </div>
              </div>
              <div
                ref={(el) => {
                  scrollRefs.current[i] = el;
                }}
                className="overflow-auto max-h-[80vh]"
              >
                <div
                  className="relative inline-block"
                  style={{
                    width: `${pageZoom * 100}%`,
                    cursor: selecting || addingLabel ? "crosshair" : "default",
                  }}
                >
                  <canvas
                    ref={(el) => {
                      canvasRefs.current[i] = el;
                    }}
                    className="block w-full h-auto"
                  />
                  <canvas
                    ref={(el) => {
                      overlayRefs.current[i] = el;
                    }}
                    className="absolute inset-0"
                    style={{ width: "100%", height: "100%", touchAction: selecting || addingLabel ? "none" : "auto", pointerEvents: selecting || addingLabel ? "auto" : "none" }}
                    onPointerDown={(e) => onPointerDown(e, i)}
                    onPointerMove={(e) => onPointerMove(e, i)}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                  />
                </div>
              </div>
              {(pageMatches[i]?.length ?? 0) > 0 && (
                <div className="border-t border-border px-4 py-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Labels — click to jump, X to delete (auto re-numbers)
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-48 overflow-auto">
                    {(() => {
                      const perTypeIdx: Record<string, number> = {};
                      return pageMatches[i].map((m, mi) => {
                        const t = typeById(m.typeId);
                        const color = t?.color ?? "#10b981";
                        perTypeIdx[m.typeId] = (perTypeIdx[m.typeId] ?? 0) + 1;
                        const num = perTypeIdx[m.typeId];
                        const isHi = highlight?.pageIndex === i && highlight.matchIndex === mi;
                        return (
                          <span
                            key={mi}
                            className={`group inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-mono transition-colors ${
                              isHi ? "ring-2 ring-amber-500" : "hover:bg-accent"
                            }`}
                            style={{ borderColor: color, background: isHi ? undefined : `${color}1a` }}
                          >
                            <span className="h-2 w-2 rounded-sm" style={{ background: color }} />
                            <button
                              type="button"
                              onClick={() => jumpToLabel(i, mi)}
                              className="font-semibold"
                              title={t?.name}
                            >
                            {t?.name ?? "?"} #{i + 1}-{num}
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteMatch(i, mi)}
                              aria-label={`Delete label ${i + 1}-${num}`}
                              className="opacity-50 hover:opacity-100 hover:text-destructive"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {previewOpen && selection && (
        <ZoomConfirmModal
          source={sourceCanvases.current[selection.pageIndex]}
          rect={selection.rect}
          zoom={previewZoom}
          onZoomChange={setPreviewZoom}
          onAccept={acceptSelection}
          onDecline={declineSelection}
        />
      )}

      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Push counts to estimate</DialogTitle>
            <DialogDescription>
              Add each icon type as a line item under "New Construction Prewire". Prices start at $0 — adjust them in the estimator.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Line items to add</div>
              <ul className="space-y-1">
                {types
                  .map((t) => ({
                    t,
                    count: pageMatches.reduce((s, arr) => s + arr.filter((m) => m.typeId === t.id).length, 0),
                  }))
                  .filter((x) => x.count > 0)
                  .map((x) => (
                    <li key={x.t.id} className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2">
                        <span className="h-3 w-3 rounded-sm" style={{ background: x.t.color }} />
                        {x.t.name}
                      </span>
                      <span className="font-mono">× {x.count}</span>
                    </li>
                  ))}
              </ul>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPushMode("new")}
                className={`flex-1 rounded-md border px-3 py-2 text-sm ${pushMode === "new" ? "border-brand bg-accent" : "border-border hover:bg-muted"}`}
              >
                New estimate
              </button>
              <button
                type="button"
                onClick={() => setPushMode("existing")}
                disabled={pushList.length === 0 && !pushListLoading}
                className={`flex-1 rounded-md border px-3 py-2 text-sm disabled:opacity-50 ${pushMode === "existing" ? "border-brand bg-accent" : "border-border hover:bg-muted"}`}
              >
                Add to existing
              </button>
            </div>

            {pushMode === "new" ? (
              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-wider text-muted-foreground">Client name</label>
                <input
                  type="text"
                  value={pushNewClient}
                  onChange={(e) => setPushNewClient(e.target.value)}
                  placeholder="e.g. Smith Residence"
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
                <label className="block text-xs uppercase tracking-wider text-muted-foreground">Estimate name (optional)</label>
                <input
                  type="text"
                  value={pushNewName}
                  onChange={(e) => setPushNewName(e.target.value)}
                  placeholder="Defaults to '{client} estimate'"
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-wider text-muted-foreground">Select estimate</label>
                {pushListLoading ? (
                  <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                  </p>
                ) : pushList.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No saved estimates yet.</p>
                ) : (
                  <select
                    value={pushSelectedId}
                    onChange={(e) => setPushSelectedId(e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  >
                    {pushList.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.client_name ? `${e.client_name} — ` : ""}{e.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {pushErr && <p className="text-sm text-destructive">{pushErr}</p>}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => setPushOpen(false)}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePush}
              disabled={pushing || (pushMode === "existing" && !pushSelectedId)}
              className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-4 py-2 text-sm font-semibold text-brand-foreground shadow-glow disabled:opacity-50"
            >
              {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {pushing ? "Pushing…" : pushMode === "new" ? "Create estimate" : "Add to estimate"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveAsOpen} onOpenChange={(o) => { setSaveAsOpen(o); if (!o) setSaveErr(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{linkedEstimateId ? "Save as new project" : "Save project"}</DialogTitle>
            <DialogDescription>
              Saves the blueprint, icon types, and all placed marks so you can reopen and edit later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="block text-xs uppercase tracking-wider text-muted-foreground">Project name</label>
              <input
                type="text"
                value={saveAsName}
                onChange={(e) => setSaveAsName(e.target.value)}
                placeholder="e.g. Smith Residence — Main Floor"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs uppercase tracking-wider text-muted-foreground">Client name (optional)</label>
              <input
                type="text"
                value={saveAsClient}
                onChange={(e) => setSaveAsClient(e.target.value)}
                placeholder="e.g. Smith Residence"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            {saveErr && <p className="text-sm text-destructive">{saveErr}</p>}
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setSaveAsOpen(false)}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveAsConfirm}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-4 py-2 text-sm font-semibold text-brand-foreground shadow-glow disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving…" : "Save project"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// --- Template matching (normalized cross-correlation w/ integral images) ---

type Template = { data: Float32Array; w: number; h: number; norm: number };
type RawMatch = { x: number; y: number; w: number; h: number; score: number };

function toGray(img: ImageData): Float32Array {
  const { data, width, height } = img;
  const out = new Float32Array(width * height);
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    out[j] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  return out;
}

function integral(src: Float32Array, w: number, h: number) {
  const W = w + 1;
  const sum = new Float64Array(W * (h + 1));
  const sumSq = new Float64Array(W * (h + 1));
  for (let y = 0; y < h; y++) {
    let rowSum = 0;
    let rowSumSq = 0;
    for (let x = 0; x < w; x++) {
      const v = src[y * w + x];
      rowSum += v;
      rowSumSq += v * v;
      sum[(y + 1) * W + (x + 1)] = sum[y * W + (x + 1)] + rowSum;
      sumSq[(y + 1) * W + (x + 1)] = sumSq[y * W + (x + 1)] + rowSumSq;
    }
  }
  return { sum, sumSq, W };
}

function rectSum(int: Float64Array, W: number, x: number, y: number, w: number, h: number) {
  return (
    int[(y + h) * W + (x + w)] -
    int[y * W + (x + w)] -
    int[(y + h) * W + x] +
    int[y * W + x]
  );
}

function extractTemplate(img: ImageData, sel: Rect): Template {
  const W_img = img.width;
  const H_img = img.height;
  const gray = toGray(img);
  const tw = sel.w;
  const th = sel.h;
  if (sel.x < 0 || sel.y < 0 || sel.x + tw > W_img || sel.y + th > H_img) {
    throw new Error("Selection out of bounds.");
  }
  const tmpl = new Float32Array(tw * th);
  let tSum = 0;
  for (let y = 0; y < th; y++) {
    for (let x = 0; x < tw; x++) {
      const v = gray[(sel.y + y) * W_img + (sel.x + x)];
      tmpl[y * tw + x] = v;
      tSum += v;
    }
  }
  const tMean = tSum / (tw * th);
  let tVar = 0;
  for (let i = 0; i < tmpl.length; i++) {
    const d = tmpl[i] - tMean;
    tmpl[i] = d;
    tVar += d * d;
  }
  const tNorm = Math.sqrt(tVar);
  if (tNorm < 1e-3) throw new Error("Selected icon has no contrast.");
  return { data: tmpl, w: tw, h: th, norm: tNorm };
}

// Rotate a (mean-subtracted) template by k * 90° clockwise. Norm is preserved.
function rotateTemplate(t: Template, k: number): Template {
  const turns = ((k % 4) + 4) % 4;
  if (turns === 0) return t;
  const { data, w, h, norm } = t;
  let nw = w, nh = h;
  if (turns === 1 || turns === 3) { nw = h; nh = w; }
  const out = new Float32Array(data.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const v = data[y * w + x];
      let nx = 0, ny = 0;
      if (turns === 1) { nx = h - 1 - y; ny = x; }
      else if (turns === 2) { nx = w - 1 - x; ny = h - 1 - y; }
      else { nx = y; ny = w - 1 - x; }
      out[ny * nw + nx] = v;
    }
  }
  return { data: out, w: nw, h: nh, norm };
}

function downsample2x(src: Float32Array, w: number, h: number) {
  const nw = w >> 1;
  const nh = h >> 1;
  const out = new Float32Array(nw * nh);
  for (let y = 0; y < nh; y++) {
    const sy = y * 2;
    const row0 = sy * w;
    const row1 = (sy + 1) * w;
    for (let x = 0; x < nw; x++) {
      const sx = x * 2;
      out[y * nw + x] = (src[row0 + sx] + src[row0 + sx + 1] + src[row1 + sx] + src[row1 + sx + 1]) * 0.25;
    }
  }
  return { data: out, w: nw, h: nh };
}

function downsampleTemplate2x(t: Template): Template | null {
  const nw = t.w >> 1;
  const nh = t.h >> 1;
  if (nw < 6 || nh < 6) return null;
  const out = new Float32Array(nw * nh);
  let sum = 0;
  for (let y = 0; y < nh; y++) {
    const sy = y * 2;
    for (let x = 0; x < nw; x++) {
      const sx = x * 2;
      const v =
        (t.data[sy * t.w + sx] +
          t.data[sy * t.w + sx + 1] +
          t.data[(sy + 1) * t.w + sx] +
          t.data[(sy + 1) * t.w + sx + 1]) * 0.25;
      out[y * nw + x] = v;
      sum += v;
    }
  }
  const mean = sum / out.length;
  let v = 0;
  for (let i = 0; i < out.length; i++) {
    out[i] -= mean;
    v += out[i] * out[i];
  }
  const norm = Math.sqrt(v);
  if (norm < 1e-3) return null;
  return { data: out, w: nw, h: nh, norm };
}

// Cosine similarity between two same-shape (mean-subtracted) templates.
function templateSimilarity(a: Template, b: Template): number {
  if (a.w !== b.w || a.h !== b.h) return 0;
  let dot = 0;
  for (let i = 0; i < a.data.length; i++) dot += a.data[i] * b.data[i];
  return dot / (a.norm * b.norm);
}

function makeScorer(
  gray: Float32Array,
  W_img: number,
  iSum: Float64Array,
  iSumSq: Float64Array,
  WS: number,
  tmpl: Float32Array,
  tw: number,
  th: number,
  tNorm: number,
) {
  const N = tw * th;
  return (x: number, y: number) => {
    const s = rectSum(iSum, WS, x, y, tw, th);
    const ss = rectSum(iSumSq, WS, x, y, tw, th);
    const mean = s / N;
    const varW = ss - s * mean;
    if (varW < 1) return -1;
    const wNorm = Math.sqrt(varW);
    let dot = 0;
    for (let j = 0; j < th; j++) {
      const ir = (y + j) * W_img + x;
      const tr = j * tw;
      for (let i = 0; i < tw; i++) dot += gray[ir + i] * tmpl[tr + i];
    }
    return dot / (wNorm * tNorm);
  };
}

function hillClimb(
  scoreAt: (x: number, y: number) => number,
  x0: number,
  y0: number,
  xMax: number,
  yMax: number,
) {
  let bx = Math.max(0, Math.min(xMax, x0));
  let by = Math.max(0, Math.min(yMax, y0));
  let bs = scoreAt(bx, by);
  for (let iter = 0; iter < 40; iter++) {
    let moved = false;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const nx = bx + dx;
        const ny = by + dy;
        if (nx < 0 || ny < 0 || nx > xMax || ny > yMax) continue;
        const s = scoreAt(nx, ny);
        if (s > bs) {
          bs = s;
          bx = nx;
          by = ny;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  return { x: bx, y: by, score: bs };
}

async function templateMatchMulti(
  img: ImageData,
  templates: Template[],
  threshold: number,
  onProgress: (p: number) => void,
): Promise<RawMatch[]> {
  const W_img = img.width;
  const H_img = img.height;
  const gray = toGray(img);
  const { sum: iSum, sumSq: iSumSq, W: WS } = integral(gray, W_img, H_img);

  // Half-res pyramid for the coarse pass (4x fewer pixels to scan).
  const half = downsample2x(gray, W_img, H_img);
  const halfInt = integral(half.data, half.w, half.h);

  // Skip rotations that are near-duplicates of an earlier template
  // (symmetric icons benefit massively — circles/squares only need 1 pass).
  const uniqueTemplates: Template[] = [];
  for (const t of templates) {
    const dup = uniqueTemplates.some((u) => templateSimilarity(t, u) > 0.97);
    if (!dup) uniqueTemplates.push(t);
  }

  const all: RawMatch[] = [];
  const coarseThreshold = Math.max(0.4, threshold - 0.1);

  for (let ti = 0; ti < uniqueTemplates.length; ti++) {
    const template = uniqueTemplates[ti];
    const { data: tmplFull, w: tw, h: th, norm: tNormFull } = template;
    if (tw > W_img || th > H_img) continue;

    const scoreFull = makeScorer(gray, W_img, iSum, iSumSq, WS, tmplFull, tw, th, tNormFull);

    // Build half-res template if it's still meaningful; otherwise fall back to full-res coarse.
    const tHalf = downsampleTemplate2x(template);
    let coarse: { x: number; y: number }[] = [];

    if (tHalf) {
      const scoreHalf = makeScorer(half.data, half.w, halfInt.sum, halfInt.sumSq, halfInt.W, tHalf.data, tHalf.w, tHalf.h, tHalf.norm);
      const step = Math.max(1, Math.floor(Math.min(tHalf.w, tHalf.h) / 10));
      const xMaxH = half.w - tHalf.w;
      const yMaxH = half.h - tHalf.h;
      const yChunk = Math.max(step, Math.floor(yMaxH / 16));
      for (let yStart = 0; yStart <= yMaxH; yStart += yChunk) {
        const yEnd = Math.min(yStart + yChunk, yMaxH);
        for (let y = yStart; y <= yEnd; y += step) {
          for (let x = 0; x <= xMaxH; x += step) {
            if (scoreHalf(x, y) >= coarseThreshold) coarse.push({ x: x * 2, y: y * 2 });
          }
        }
        const subPart = yMaxH === 0 ? 1 : Math.min(1, yEnd / yMaxH);
        onProgress(Math.min(1, (ti + subPart * 0.85) / uniqueTemplates.length));
        await new Promise((r) => setTimeout(r, 0));
      }
    } else {
      const step = Math.max(1, Math.floor(Math.min(tw, th) / 12));
      const xMax = W_img - tw;
      const yMax = H_img - th;
      const yChunk = Math.max(step, Math.floor(yMax / 16));
      for (let yStart = 0; yStart <= yMax; yStart += yChunk) {
        const yEnd = Math.min(yStart + yChunk, yMax);
        for (let y = yStart; y <= yEnd; y += step) {
          for (let x = 0; x <= xMax; x += step) {
            if (scoreFull(x, y) >= coarseThreshold) coarse.push({ x, y });
          }
        }
        const subPart = yMax === 0 ? 1 : Math.min(1, yEnd / yMax);
        onProgress(Math.min(1, (ti + subPart * 0.85) / uniqueTemplates.length));
        await new Promise((r) => setTimeout(r, 0));
      }
    }

    // Hill-climb to the true local maximum at full resolution.
    const xMax = W_img - tw;
    const yMax = H_img - th;
    for (const c of coarse) {
      const best = hillClimb(scoreFull, c.x, c.y, xMax, yMax);
      if (best.score >= threshold) all.push({ x: best.x, y: best.y, w: tw, h: th, score: best.score });
    }
    onProgress((ti + 1) / uniqueTemplates.length);
    await new Promise((r) => setTimeout(r, 0));
  }

  // Non-max suppression across all rotations
  all.sort((a, b) => b.score - a.score);
  const kept: RawMatch[] = [];
  for (const c of all) {
    const ccx = c.x + c.w / 2;
    const ccy = c.y + c.h / 2;
    let ok = true;
    for (const k of kept) {
      const kcx = k.x + k.w / 2;
      const kcy = k.y + k.h / 2;
      const minD = Math.max(c.w, c.h, k.w, k.h) * 0.45;
      if (Math.abs(ccx - kcx) < minD && Math.abs(ccy - kcy) < minD) {
        ok = false;
        break;
      }
    }
    if (ok) kept.push(c);
  }

  const avgH = kept.reduce((s, m) => s + m.h, 0) / Math.max(1, kept.length);
  kept.sort((a, b) => (Math.abs(a.y - b.y) < avgH * 0.5 ? a.x - b.x : a.y - b.y));
  return kept;
}

type Handle = "move" | "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

function ZoomConfirmModal({
  source,
  rect,
  zoom,
  onZoomChange,
  onAccept,
  onDecline,
}: {
  source: HTMLCanvasElement | undefined;
  rect: Rect;
  zoom: number;
  onZoomChange: (z: number) => void;
  onAccept: (rect: Rect) => void;
  onDecline: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [editRect, setEditRect] = useState<Rect>(rect);
  const dragRef = useRef<{ handle: Handle; startX: number; startY: number; orig: Rect } | null>(null);

  // Reset when a new selection opens the modal
  useEffect(() => {
    setEditRect(rect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rect.x, rect.y, rect.w, rect.h]);

  // Padded view region in source pixels, locked to the original rect so the
  // canvas size stays stable while the user resizes the crop.
  const pad = Math.max(20, Math.round(Math.max(rect.w, rect.h) * 0.6));
  const view = {
    x: Math.max(0, Math.round(rect.x - pad)),
    y: Math.max(0, Math.round(rect.y - pad)),
    w: 0,
    h: 0,
  };
  if (source) {
    const maxW = Math.min(source.width - view.x, Math.round(rect.w + pad * 2));
    const maxH = Math.min(source.height - view.y, Math.round(rect.h + pad * 2));
    view.w = maxW;
    view.h = maxH;
  }

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !source || view.w <= 0 || view.h <= 0) return;
    const w = view.w * zoom;
    const h = view.h * zoom;
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d")!;
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(source, view.x, view.y, view.w, view.h, 0, 0, w, h);

    // Draw selection rect in display coords
    const rx = (editRect.x - view.x) * zoom;
    const ry = (editRect.y - view.y) * zoom;
    const rw = editRect.w * zoom;
    const rh = editRect.h * zoom;

    // Dim outside
    ctx.fillStyle = "rgba(0,0,0,0.35)";
    ctx.fillRect(0, 0, w, ry);
    ctx.fillRect(0, ry + rh, w, h - (ry + rh));
    ctx.fillRect(0, ry, rx, rh);
    ctx.fillRect(rx + rw, ry, w - (rx + rw), rh);

    ctx.strokeStyle = "#ef4444";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(rx, ry, rw, rh);
    ctx.setLineDash([]);

    // Handles
    const hs = 10;
    const pts: Array<[number, number]> = [
      [rx, ry], [rx + rw / 2, ry], [rx + rw, ry],
      [rx, ry + rh / 2], [rx + rw, ry + rh / 2],
      [rx, ry + rh], [rx + rw / 2, ry + rh], [rx + rw, ry + rh],
    ];
    ctx.fillStyle = "#ef4444";
    pts.forEach(([px, py]) => ctx.fillRect(px - hs / 2, py - hs / 2, hs, hs));
  }, [source, view.x, view.y, view.w, view.h, zoom, editRect.x, editRect.y, editRect.w, editRect.h]);

  function toSource(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = canvasRef.current!;
    const b = c.getBoundingClientRect();
    const sx = c.width / b.width;
    const sy = c.height / b.height;
    const cx = (e.clientX - b.left) * sx;
    const cy = (e.clientY - b.top) * sy;
    return { x: cx / zoom + view.x, y: cy / zoom + view.y };
  }

  function hitTest(px: number, py: number, touch = false): Handle | null {
    const r = editRect;
    const t = (touch ? 14 : 6) / zoom; // larger tolerance on touch
    const nearL = Math.abs(px - r.x) < t;
    const nearR = Math.abs(px - (r.x + r.w)) < t;
    const nearT = Math.abs(py - r.y) < t;
    const nearB = Math.abs(py - (r.y + r.h)) < t;
    const insideX = px > r.x - t && px < r.x + r.w + t;
    const insideY = py > r.y - t && py < r.y + r.h + t;
    if (nearT && nearL) return "nw";
    if (nearT && nearR) return "ne";
    if (nearB && nearL) return "sw";
    if (nearB && nearR) return "se";
    if (nearT && insideX) return "n";
    if (nearB && insideX) return "s";
    if (nearL && insideY) return "w";
    if (nearR && insideY) return "e";
    if (px > r.x && px < r.x + r.w && py > r.y && py < r.y + r.h) return "move";
    return null;
  }

  function cursorFor(h: Handle | null): string {
    switch (h) {
      case "move": return "move";
      case "n": case "s": return "ns-resize";
      case "e": case "w": return "ew-resize";
      case "ne": case "sw": return "nesw-resize";
      case "nw": case "se": return "nwse-resize";
      default: return "default";
    }
  }

  const [hoverCursor, setHoverCursor] = useState("default");

  function onDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    const touch = e.pointerType !== "mouse";
    const p = toSource(e);
    let h = hitTest(p.x, p.y, touch);
    if (!h && touch) {
      // Tap outside: snap rect center to tap point so user can drag immediately
      const r = editRect;
      const nr = {
        x: Math.max(0, p.x - r.w / 2),
        y: Math.max(0, p.y - r.h / 2),
        w: r.w,
        h: r.h,
      };
      if (source) {
        nr.x = Math.min(source.width - nr.w, nr.x);
        nr.y = Math.min(source.height - nr.h, nr.y);
      }
      setEditRect(nr);
      h = "move";
      dragRef.current = { handle: h, startX: p.x, startY: p.y, orig: { ...nr } };
    } else {
      if (!h) return;
      dragRef.current = { handle: h, startX: p.x, startY: p.y, orig: { ...editRect } };
    }
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  }
  function onMove(e: React.PointerEvent<HTMLCanvasElement>) {
    const p = toSource(e);
    if (!dragRef.current) {
      setHoverCursor(cursorFor(hitTest(p.x, p.y)));
      return;
    }
    e.preventDefault();
    const { handle, startX, startY, orig } = dragRef.current;
    const dx = p.x - startX;
    const dy = p.y - startY;
    let nr = { ...orig };
    const minSize = 4;
    if (handle === "move") {
      nr.x = orig.x + dx;
      nr.y = orig.y + dy;
    } else {
      if (handle.includes("w")) { nr.x = orig.x + dx; nr.w = orig.w - dx; }
      if (handle.includes("e")) { nr.w = orig.w + dx; }
      if (handle.includes("n")) { nr.y = orig.y + dy; nr.h = orig.h - dy; }
      if (handle.includes("s")) { nr.h = orig.h + dy; }
      if (nr.w < minSize) { nr.x = nr.x + nr.w - minSize; nr.w = minSize; }
      if (nr.h < minSize) { nr.y = nr.y + nr.h - minSize; nr.h = minSize; }
    }
    if (source) {
      nr.x = Math.max(0, Math.min(source.width - minSize, nr.x));
      nr.y = Math.max(0, Math.min(source.height - minSize, nr.y));
      nr.w = Math.max(minSize, Math.min(source.width - nr.x, nr.w));
      nr.h = Math.max(minSize, Math.min(source.height - nr.y, nr.h));
    }
    setEditRect(nr);
  }
  function onUp() {
    dragRef.current = null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6">
      <div className="w-full max-w-3xl rounded-2xl border border-border bg-card shadow-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Confirm icon selection</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Drag the edges, corners, or inside the box to fine-tune the crop.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            Zoom
            <input
              type="range"
              min={2}
              max={24}
              step={1}
              value={zoom}
              onChange={(e) => onZoomChange(parseInt(e.target.value, 10))}
            />
            <span className="font-mono text-xs">{zoom}x</span>
          </label>
        </div>
        <div className="bg-muted/40 p-4 max-h-[60vh] overflow-auto flex items-center justify-center">
          <canvas
            ref={canvasRef}
            className="block border border-border select-none"
            style={{ imageRendering: "pixelated", touchAction: "none", cursor: dragRef.current ? cursorFor(dragRef.current.handle) : hoverCursor }}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
          />
        </div>
        <div className="flex items-center justify-between border-t border-border px-5 py-3">
          <div className="text-xs text-muted-foreground font-mono">
            {Math.round(editRect.w)} × {Math.round(editRect.h)} px
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onDecline}
              className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              Decline
            </button>
            <button
              onClick={() => onAccept(editRect)}
              className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-5 py-2 text-sm font-semibold text-brand-foreground shadow-glow"
            >
              Accept selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
